import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  CreditCard,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Users,
  Search,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  IndianRupee,
  ExternalLink,
  ShoppingCart,
  Sparkles,
  Calendar,
  Download,
  Receipt,
  FileText,
  Plus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BankReconciliation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [statements, setStatements] = useState([]);
  const [outstandingReport, setOutstandingReport] = useState(null);
  const [payablesReport, setPayablesReport] = useState(null);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("receivables");

  // For transactions view
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [statementTransactions, setStatementTransactions] = useState(null);
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [transactionType, setTransactionType] = useState("credit");
  const [savingMapping, setSavingMapping] = useState(null);
  const [transactionSearch, setTransactionSearch] = useState("");
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [bulkMappingBuyer, setBulkMappingBuyer] = useState("");
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [statementsRes, reportRes, payablesRes] = await Promise.all([
        axios.get(`${API}/bank-statement/list`, { headers }),
        axios.get(`${API}/bank-reconciliation/outstanding`, { headers }),
        axios.get(`${API}/bank-reconciliation/payables`, { headers }),
      ]);

      setStatements(statementsRes.data);
      setOutstandingReport(reportRes.data);
      setPayablesReport(payablesRes.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load data");
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [".pdf", ".xlsx", ".xls", ".csv"];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf("."));

    if (!validTypes.includes(fileExt)) {
      toast.error("Please upload a PDF, Excel (.xlsx, .xls) or CSV file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/bank-statement/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toast.success(
        `Bank statement processed! Found ${response.data.transactions_count} transactions`,
      );
      loadData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to upload bank statement",
      );
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDeleteStatement = async (statementId) => {
    if (!window.confirm("Are you sure you want to delete this bank statement?"))
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/bank-statement/${statementId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Bank statement deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete statement");
    }
  };

  const openTransactionsDialog = async (statement, type = "credit") => {
    setSelectedStatement(statement);
    setTransactionType(type);
    setTransactionsDialogOpen(true);
    setTransactionSearch("");
    setSelectedTransactions([]);

    try {
      const token = localStorage.getItem("token");
      const endpoint =
        type === "credit"
          ? `${API}/bank-statement/${statement.id}/transactions`
          : `${API}/bank-statement/${statement.id}/debit-transactions`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatementTransactions(response.data);
    } catch (error) {
      toast.error("Failed to load transactions");
    }
  };

  const handleMapTransaction = async (transactionIndex, partyName) => {
    if (!selectedStatement) return;

    setSavingMapping(transactionIndex);
    try {
      const token = localStorage.getItem("token");
      const endpoint =
        transactionType === "credit"
          ? `${API}/bank-statement/map-transaction`
          : `${API}/bank-statement/map-payable`;

      const payload =
        transactionType === "credit"
          ? {
              statement_id: selectedStatement.id,
              transaction_index: transactionIndex,
              buyer_name: partyName || null,
            }
          : {
              statement_id: selectedStatement.id,
              transaction_index: transactionIndex,
              supplier_name: partyName || null,
            };

      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const mappingKey =
        transactionType === "credit" ? "mapped_buyer" : "mapped_supplier";
      setStatementTransactions((prev) => ({
        ...prev,
        transactions: prev.transactions.map((t) =>
          t.index === transactionIndex
            ? { ...t, [mappingKey]: partyName || null }
            : t,
        ),
      }));

      toast.success(partyName ? `Mapped to ${partyName}` : "Mapping removed");
      loadData();
    } catch (error) {
      toast.error("Failed to save mapping");
    } finally {
      setSavingMapping(null);
    }
  };

  const handleBulkMap = async () => {
    if (
      !selectedStatement ||
      selectedTransactions.length === 0 ||
      !bulkMappingBuyer
    )
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/bank-statement/bulk-map`,
        {
          statement_id: selectedStatement.id,
          transaction_indices: selectedTransactions,
          party_name: bulkMappingBuyer,
          mapping_type: transactionType === "credit" ? "receivable" : "payable",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success(
        `Mapped ${selectedTransactions.length} transactions to ${bulkMappingBuyer}`,
      );
      setBulkDialogOpen(false);
      setSelectedTransactions([]);
      setBulkMappingBuyer("");

      openTransactionsDialog(selectedStatement, transactionType);
      loadData();
    } catch (error) {
      toast.error("Failed to bulk map transactions");
    }
  };

  const toggleTransactionSelection = (index) => {
    setSelectedTransactions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const selectAllFiltered = () => {
    if (!statementTransactions) return;
    const filtered = getFilteredTransactions();
    const allIndices = filtered.map((t) => t.index);
    setSelectedTransactions(allIndices);
  };

  const clearSelection = () => {
    setSelectedTransactions([]);
  };

  const getFilteredTransactions = () => {
    if (!statementTransactions) return [];
    return statementTransactions.transactions.filter((t) => {
      const amount = transactionType === "credit" ? t.credit : t.debit;
      if (!amount || parseFloat(amount) <= 0) return false;

      if (!transactionSearch) return true;
      const search = transactionSearch.toLowerCase();
      return (
        (t.description || "").toLowerCase().includes(search) ||
        (t.party_name || "").toLowerCase().includes(search)
      );
    });
  };

  const openBuyerDetails = (buyer) => {
    setSelectedBuyer(buyer);
    setDetailsDialogOpen(true);
  };

  const openSupplierDetails = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierDialogOpen(true);
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "-";
    return `₹${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      // Handle DD/MM/YY or DD/MM/YYYY formats (e.g., "18/02/26" or "18/02/2026")
      const ddmmyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (ddmmyyMatch) {
        let [, day, month, year] = ddmmyyMatch;
        if (year.length === 2) year = `20${year}`;
        const parsed = new Date(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`);
        if (!isNaN(parsed)) {
          return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
        }
      }
      // Fallback for ISO or other standard formats
      const parsed = new Date(dateStr);
      if (!isNaN(parsed)) {
        return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const filteredBuyers =
    outstandingReport?.buyers?.filter((buyer) => {
      if (!searchTerm) return true;
      return buyer.buyer_name.toLowerCase().includes(searchTerm.toLowerCase());
    }) || [];

  const filteredSuppliers =
    payablesReport?.suppliers?.filter((supplier) => {
      if (!searchTerm) return true;
      return supplier.supplier_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    }) || [];

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[300px] space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="text-gray-500 text-sm">Loading bank data...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="bank-reconciliation-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-500 to-orange-500 bg-clip-text text-transparent animate-gradient">
              Bank Reconciliation
            </h1>
            <p className="text-gray-500 text-xs mt-0.5">
              Track receivables (from buyers) and payables (to suppliers)
            </p>
          </div>

          {/* Upload Button - Hidden for Organizations */}
          {JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" && (
          <div className="flex items-center gap-2">
            <Badge className="text-xs py-0.5 px-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-sm">
              <Sparkles size={10} className="mr-1" />
              AI Powered
            </Badge>
            <input
              type="file"
              id="bank-statement-upload"
              accept=".pdf,.xlsx,.xls,.csv"
              onChange={handleUpload}
              className="hidden"
            />
            <Button
              onClick={() =>
                document.getElementById("bank-statement-upload")?.click()
              }
              disabled={uploading}
              className="h-8 bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white text-xs"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Upload size={12} className="mr-1.5" />
                  Upload Statement
                </>
              )}
            </Button>
          </div>
          )}
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 bg-gray-100 p-0.5 rounded-lg mb-14 sm:mb-0">
            <TabsTrigger
              value="receivables"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-500 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md"
            >
              <div className="p-0.5 rounded bg-white/20">
                <TrendingUp size={12} />
              </div>
              Sales
            </TabsTrigger>
            <TabsTrigger
              value="payables"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md"
            >
              <div className="p-0.5 rounded bg-white/20">
                <ShoppingCart size={12} />
              </div>
              Purchase
            </TabsTrigger>
            <TabsTrigger
              value="transactions"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md"
            >
              <div className="p-0.5 rounded bg-white/20">
                <FileText size={12} />
              </div>
              Transactions
            </TabsTrigger>
            <TabsTrigger
              value="statements"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium data-[state=active]:bg-gradient-to-r data-[state=active]:from-gray-700 data-[state=active]:to-gray-800 data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md"
            >
              <div className="p-0.5 rounded bg-white/20">
                <FileSpreadsheet size={12} />
              </div>
              Statements
            </TabsTrigger>
          </TabsList>

          {/* Receivables Tab (Sales) */}
          <TabsContent value="receivables" className="mt-3 space-y-3">
            {/* Stats Cards with Enhanced Gradients */}
            {outstandingReport?.summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  {
                    label: "Total Sales",
                    value: outstandingReport.summary.total_sales,
                    gradient: "from-green-400 to-green-500",
                    icon: TrendingUp,
                    hoverGradient: "hover:from-green-500 hover:to-green-600",
                    textColor: "text-white",
                  },
                  {
                    label: "Received",
                    value: outstandingReport.summary.total_received,
                    gradient: "from-primary-400 to-primary-500",
                    icon: IndianRupee,
                    hoverGradient:
                      "hover:from-primary-500 hover:to-primary-600",
                    textColor: "text-white",
                  },
                  {
                    label: "Outstanding",
                    value: outstandingReport.summary.total_outstanding,
                    gradient: "from-red-400 to-red-500",
                    icon: AlertCircle,
                    hoverGradient: "hover:from-red-500 hover:to-red-600",
                    textColor: "text-white",
                  },
                  {
                    label: "Buyers",
                    value: outstandingReport.summary.buyer_count,
                    gradient: "from-gray-400 to-gray-500",
                    icon: Users,
                    hoverGradient: "hover:from-gray-500 hover:to-gray-600",
                    textColor: "text-white",
                  },
                ].map((stat, index) => (
                  <Card
                    key={index}
                    className={`border-0 overflow-hidden group transition-all duration-300 relative ${stat.textColor}`}
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-90 group-hover:opacity-100 transition-opacity duration-300`}
                    ></div>

                    <CardContent className="p-3 relative z-10">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium opacity-95">
                          {stat.label}
                        </span>
                        <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                          <stat.icon size={12} />
                        </div>
                      </div>
                      <div className="text-lg font-bold">
                        {stat.label === "Buyers"
                          ? stat.value
                          : formatCurrency(stat.value)}
                      </div>
                      <div className="text-[10px] opacity-90 mt-0.5">
                        {stat.label !== "Buyers" ? "Cumulative" : "Active"}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Search Section - Enhanced */}
            <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-gradient-to-br from-green-400 to-green-500">
                        <TrendingUp size={14} className="text-white" />
                      </div>
                      Receivables
                    </CardTitle>
                    <CardDescription className="text-xs text-gray-500">
                      Track payments from customers
                    </CardDescription>
                  </div>
                  <div className="relative w-full sm:w-48">
                    <Search
                      size={12}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-green-500 transition-colors"
                    />
                    <Input
                      placeholder="Search buyers by name or GST..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 h-8 text-xs bg-white border-gray-300 focus:border-green-500 focus:ring-1 focus:ring-green-500/20"
                    />
                  </div>
                </div>
              </CardHeader>

              {/* Buyers List */}
              {filteredBuyers.length === 0 ? (
                <CardContent className="pt-4">
                  <div className="text-center py-6">
                    <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mb-3">
                      <TrendingUp size={18} className="text-green-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-600 mb-1">
                      No receivables found
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload sales invoices and bank statements to get started
                    </p>
                    {JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" && (
                    <Button
                      onClick={() => navigate("/invoices")}
                      className="h-7 text-xs bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                    >
                      <Upload size={10} className="mr-1.5" />
                      Upload Invoices
                    </Button>
                    )}
                  </div>
                </CardContent>
              ) : (
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {/* Mobile View */}
                    <div className="sm:hidden">
                      {filteredBuyers.map((buyer, index) => (
                        <div
                          key={index}
                          className="relative overflow-hidden rounded-xl bg-white border border-gray-200 hover:border-green-300 hover:shadow-sm transition-all duration-300 cursor-pointer group"
                          onClick={() => openBuyerDetails(buyer)}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-green-50/0 to-emerald-50/0 group-hover:from-green-50/30 group-hover:to-emerald-50/30 transition-all duration-300"></div>

                          <div className="relative p-3">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2">
                                <div className="p-2 rounded-lg bg-gradient-to-br from-green-100 to-emerald-100">
                                  <Users size={14} className="text-green-600" />
                                </div>
                                <div className="min-w-0">
                                  <h4 className="text-xs font-semibold text-gray-800 truncate">
                                    {buyer.buyer_name}
                                  </h4>
                                  {buyer.buyer_gst && (
                                    <p className="text-[10px] text-gray-500 font-mono mt-0.5">
                                      {buyer.buyer_gst}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <Badge
                                className={`text-[10px] px-2 py-0.5 ${
                                  buyer.outstanding <= 0
                                    ? "bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-l-2 border-green-500 text-green-700"
                                    : buyer.total_received > 0
                                      ? "bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-l-2 border-orange-500 text-orange-700"
                                      : "bg-gradient-to-br from-red-500/10 to-red-500/5 border-l-2 border-red-500 text-red-700"
                                }`}
                              >
                                {buyer.outstanding <= 0
                                  ? "Cleared"
                                  : buyer.total_received > 0
                                    ? "Partial"
                                    : "Pending"}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <div className="text-[10px] text-gray-500">
                                  Total
                                </div>
                                <div className="text-xs font-bold text-green-600">
                                  {formatCurrency(buyer.total_sales)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] text-gray-500">
                                  Received
                                </div>
                                <div className="text-xs font-bold text-primary-600">
                                  {formatCurrency(buyer.total_received)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] text-gray-500">
                                  Due
                                </div>
                                <div className="text-xs font-bold text-red-600">
                                  {formatCurrency(buyer.outstanding)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop View */}
                    <div className="hidden sm:block overflow-hidden rounded-lg border border-gray-200">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gradient-to-r from-green-50/50 to-emerald-50/30">
                            <TableHead className="text-xs text-gray-600 p-3">
                              Buyer
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3">
                              GST
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3 text-right">
                              Total
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3 text-right">
                              Received
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3 text-right">
                              Due
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3 text-center">
                              Status
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredBuyers.map((buyer, index) => (
                            <TableRow
                              key={index}
                              className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-green-50/30 hover:to-emerald-50/10 cursor-pointer group transition-colors"
                              onClick={() => openBuyerDetails(buyer)}
                            >
                              <TableCell className="p-3">
                                <div className="flex items-center gap-2">
                                  <div className="p-1.5 rounded-md bg-gradient-to-br from-green-100 to-emerald-100">
                                    <Users
                                      size={12}
                                      className="text-green-600"
                                    />
                                  </div>
                                  <div className="text-xs font-medium text-gray-800">
                                    {buyer.buyer_name}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="p-3">
                                {buyer.buyer_gst ? (
                                  <code className="text-[10px] bg-gray-100 px-2 py-1 rounded font-mono">
                                    {buyer.buyer_gst}
                                  </code>
                                ) : (
                                  <span className="text-xs text-gray-400">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="p-3 text-right">
                                <div className="text-xs font-bold bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">
                                  {formatCurrency(buyer.total_sales)}
                                </div>
                              </TableCell>
                              <TableCell className="p-3 text-right">
                                <div className="text-xs font-bold bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent">
                                  {formatCurrency(buyer.total_received)}
                                </div>
                              </TableCell>
                              <TableCell className="p-3 text-right">
                                <div className="text-xs font-bold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                                  {formatCurrency(buyer.outstanding)}
                                </div>
                              </TableCell>
                              <TableCell className="p-3">
                                <div className="flex justify-center">
                                  <Badge
                                    className={`text-[10px] px-2 py-0.5 ${
                                      buyer.outstanding <= 0
                                        ? "bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-l-2 border-green-500 text-green-700"
                                        : buyer.total_received > 0
                                          ? "bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-l-2 border-orange-500 text-orange-700"
                                          : "bg-gradient-to-br from-red-500/10 to-red-500/5 border-l-2 border-red-500 text-red-700"
                                    }`}
                                  >
                                    {buyer.outstanding <= 0 ? (
                                      <div className="flex items-center gap-1">
                                        <CheckCircle size={10} />
                                        <span>Cleared</span>
                                      </div>
                                    ) : buyer.total_received > 0 ? (
                                      "Partial"
                                    ) : (
                                      <div className="flex items-center gap-1">
                                        <XCircle size={10} />
                                        <span>Pending</span>
                                      </div>
                                    )}
                                  </Badge>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          </TabsContent>

          {/* Payables Tab (Purchases) */}
          <TabsContent value="payables" className="mt-3 space-y-3">
            {/* Stats Cards with Enhanced Gradients */}
            {payablesReport?.summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  {
                    label: "Total Purchases",
                    value: payablesReport.summary.total_purchases,
                    gradient: "from-red-400 to-red-500",
                    icon: ShoppingCart,
                    hoverGradient: "hover:from-red-500 hover:to-red-600",
                    textColor: "text-white",
                  },
                  {
                    label: "Paid",
                    value: payablesReport.summary.total_paid,
                    gradient: "from-primary-400 to-primary-500",
                    icon: IndianRupee,
                    hoverGradient:
                      "hover:from-primary-500 hover:to-primary-600",
                    textColor: "text-white",
                  },
                  {
                    label: "Balance Due",
                    value: payablesReport.summary.total_balance_due,
                    gradient: "from-orange-400 to-orange-500",
                    icon: AlertCircle,
                    hoverGradient: "hover:from-orange-500 hover:to-orange-600",
                    textColor: "text-white",
                  },
                  {
                    label: "Suppliers",
                    value: payablesReport.summary.supplier_count,
                    gradient: "from-gray-400 to-gray-500",
                    icon: Users,
                    hoverGradient: "hover:from-gray-500 hover:to-gray-600",
                    textColor: "text-white",
                  },
                ].map((stat, index) => (
                  <Card
                    key={index}
                    className={`border-0 overflow-hidden group transition-all duration-300 relative ${stat.textColor}`}
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-90 group-hover:opacity-100 transition-opacity duration-300`}
                    ></div>

                    <CardContent className="p-3 relative z-10">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium opacity-95">
                          {stat.label}
                        </span>
                        <div className="p-1.5 rounded-md bg-white/20 backdrop-blur-sm">
                          <stat.icon size={12} />
                        </div>
                      </div>
                      <div className="text-lg font-bold">
                        {stat.label === "Suppliers"
                          ? stat.value
                          : formatCurrency(stat.value)}
                      </div>
                      <div className="text-[10px] opacity-90 mt-0.5">
                        {stat.label !== "Suppliers" ? "Cumulative" : "Active"}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Search Section - Enhanced */}
            <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-gradient-to-br from-red-400 to-red-500">
                        <ShoppingCart size={14} className="text-white" />
                      </div>
                      Payables
                    </CardTitle>
                    <CardDescription className="text-xs text-gray-500">
                      Track payments to suppliers
                    </CardDescription>
                  </div>
                  <div className="relative w-full sm:w-48">
                    <Search
                      size={12}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-red-500 transition-colors"
                    />
                    <Input
                      placeholder="Search suppliers by name or GST..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 h-8 text-xs bg-white border-gray-300 focus:border-red-500 focus:ring-1 focus:ring-red-500/20"
                    />
                  </div>
                </div>
              </CardHeader>

              {/* Suppliers List */}
              {filteredSuppliers.length === 0 ? (
                <CardContent className="pt-4">
                  <div className="text-center py-6">
                    <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center mb-3">
                      <ShoppingCart size={18} className="text-red-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-600 mb-1">
                      No payables found
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload purchase invoices and bank statements to get
                      started
                    </p>
                    {JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" && (
                    <Button
                      onClick={() => navigate("/invoices")}
                      className="h-7 text-xs bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white"
                    >
                      <Upload size={10} className="mr-1.5" />
                      Upload Invoices
                    </Button>
                    )}
                  </div>
                </CardContent>
              ) : (
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {/* Mobile View */}
                    <div className="sm:hidden">
                      {filteredSuppliers.map((supplier, index) => (
                        <div
                          key={index}
                          className="relative overflow-hidden rounded-xl bg-white border border-gray-200 hover:border-red-300 hover:shadow-sm transition-all duration-300 cursor-pointer group"
                          onClick={() => openSupplierDetails(supplier)}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-red-50/0 to-orange-50/0 group-hover:from-red-50/30 group-hover:to-orange-50/30 transition-all duration-300"></div>

                          <div className="relative p-3">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex items-center gap-2">
                                <div className="p-2 rounded-lg bg-gradient-to-br from-red-100 to-orange-100">
                                  <Users size={14} className="text-red-600" />
                                </div>
                                <div className="min-w-0">
                                  <h4 className="text-xs font-semibold text-gray-800 truncate">
                                    {supplier.supplier_name}
                                  </h4>
                                  {supplier.supplier_gst && (
                                    <p className="text-[10px] text-gray-500 font-mono mt-0.5">
                                      {supplier.supplier_gst}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <Badge
                                className={`text-[10px] px-2 py-0.5 ${
                                  supplier.balance_due <= 0
                                    ? "bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-l-2 border-green-500 text-green-700"
                                    : supplier.total_paid > 0
                                      ? "bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-l-2 border-orange-500 text-orange-700"
                                      : "bg-gradient-to-br from-red-500/10 to-red-500/5 border-l-2 border-red-500 text-red-700"
                                }`}
                              >
                                {supplier.balance_due <= 0
                                  ? "Paid"
                                  : supplier.total_paid > 0
                                    ? "Partial"
                                    : "Unpaid"}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <div className="text-[10px] text-gray-500">
                                  Total
                                </div>
                                <div className="text-xs font-bold text-red-600">
                                  {formatCurrency(supplier.total_purchases)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] text-gray-500">
                                  Paid
                                </div>
                                <div className="text-xs font-bold text-primary-600">
                                  {formatCurrency(supplier.total_paid)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] text-gray-500">
                                  Due
                                </div>
                                <div className="text-xs font-bold text-orange-600">
                                  {formatCurrency(supplier.balance_due)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop View */}
                    <div className="hidden sm:block overflow-hidden rounded-lg border border-gray-200">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gradient-to-r from-red-50/50 to-orange-50/30">
                            <TableHead className="text-xs text-gray-600 p-3">
                              Supplier
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3">
                              GST
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3 text-right">
                              Total
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3 text-right">
                              Paid
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3 text-right">
                              Due
                            </TableHead>
                            <TableHead className="text-xs text-gray-600 p-3 text-center">
                              Status
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredSuppliers.map((supplier, index) => (
                            <TableRow
                              key={index}
                              className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-red-50/30 hover:to-orange-50/10 cursor-pointer group transition-colors"
                              onClick={() => openSupplierDetails(supplier)}
                            >
                              <TableCell className="p-3">
                                <div className="flex items-center gap-2">
                                  <div className="p-1.5 rounded-md bg-gradient-to-br from-red-100 to-orange-100">
                                    <Users size={12} className="text-red-600" />
                                  </div>
                                  <div className="text-xs font-medium text-gray-800">
                                    {supplier.supplier_name}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="p-3">
                                {supplier.supplier_gst ? (
                                  <code className="text-[10px] bg-gray-100 px-2 py-1 rounded font-mono">
                                    {supplier.supplier_gst}
                                  </code>
                                ) : (
                                  <span className="text-xs text-gray-400">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="p-3 text-right">
                                <div className="text-xs font-bold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                                  {formatCurrency(supplier.total_purchases)}
                                </div>
                              </TableCell>
                              <TableCell className="p-3 text-right">
                                <div className="text-xs font-bold bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent">
                                  {formatCurrency(supplier.total_paid)}
                                </div>
                              </TableCell>
                              <TableCell className="p-3 text-right">
                                <div className="text-xs font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                                  {formatCurrency(supplier.balance_due)}
                                </div>
                              </TableCell>
                              <TableCell className="p-3">
                                <div className="flex justify-center">
                                  <Badge
                                    className={`text-[10px] px-2 py-0.5 ${
                                      supplier.balance_due <= 0
                                        ? "bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-l-2 border-green-500 text-green-700"
                                        : supplier.total_paid > 0
                                          ? "bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-l-2 border-orange-500 text-orange-700"
                                          : "bg-gradient-to-br from-red-500/10 to-red-500/5 border-l-2 border-red-500 text-red-700"
                                    }`}
                                  >
                                    {supplier.balance_due <= 0 ? (
                                      <div className="flex items-center gap-1">
                                        <CheckCircle size={10} />
                                        <span>Paid</span>
                                      </div>
                                    ) : supplier.total_paid > 0 ? (
                                      "Partial"
                                    ) : (
                                      <div className="flex items-center gap-1">
                                        <XCircle size={10} />
                                        <span>Unpaid</span>
                                      </div>
                                    )}
                                  </Badge>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="mt-3">
            <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-100">
                <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary-400 to-primary-500">
                    <FileText size={14} className="text-white" />
                  </div>
                  Bank Transactions
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  View and analyze transaction data from statements
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {statements.length === 0 ? (
                  <div className="text-center py-6">
                    <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-primary-100 to-orange-100 flex items-center justify-center mb-3">
                      <FileSpreadsheet size={18} className="text-primary-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-600 mb-1">
                      No bank statements uploaded
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload a bank statement to view transaction data
                    </p>
                    {JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" && (
                    <>
                    <input
                      type="file"
                      id="bank-statement-upload-2"
                      accept=".pdf,.xlsx,.xls,.csv"
                      onChange={handleUpload}
                      className="hidden"
                    />
                    <Button
                      onClick={() =>
                        document
                          .getElementById("bank-statement-upload-2")
                          ?.click()
                      }
                      className="h-7 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white"
                    >
                      <Upload size={10} className="mr-1.5" />
                      Upload Statement
                    </Button>
                    </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {statements.map((statement) => (
                      <div
                        key={statement.id}
                        className="relative overflow-hidden rounded-xl bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all duration-300 group"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-primary-50/0 to-orange-50/0 group-hover:from-primary-50/30 group-hover:to-orange-50/30 transition-all duration-300"></div>

                        <div className="relative p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className="p-2 rounded-lg bg-gradient-to-br from-primary-100 to-orange-100">
                                <FileSpreadsheet
                                  size={14}
                                  className="text-primary-600"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-semibold text-gray-800 truncate">
                                  {statement.filename}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex items-center gap-1 text-[10px] text-gray-500">
                                    <Calendar size={8} />
                                    <span>
                                      {formatDate(statement.upload_date)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1 text-[10px] text-green-500">
                                    <TrendingUp size={8} />
                                    <span>
                                      Credits:{" "}
                                      {formatCurrency(statement.total_credits)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1 text-[10px] text-red-500">
                                    <TrendingDown size={8} />
                                    <span>
                                      Debits:{" "}
                                      {formatCurrency(statement.total_debits)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                onClick={() =>
                                  openTransactionsDialog(statement, "credit")
                                }
                                className="h-6 text-xs bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
                              >
                                <TrendingUp size={10} className="mr-1" />
                                Credits
                              </Button>
                              <Button
                                onClick={() =>
                                  openTransactionsDialog(statement, "debit")
                                }
                                className="h-6 text-xs bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white"
                              >
                                <TrendingDown size={10} className="mr-1" />
                                Debits
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Statements Tab */}
          <TabsContent value="statements" className="mt-3">
            <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-100">
                <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-gradient-to-br from-gray-400 to-gray-500">
                    <FileSpreadsheet size={14} className="text-white" />
                  </div>
                  Bank Statements
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  Uploaded statement files and their metadata
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                {statements.length === 0 ? (
                  <div className="text-center py-6">
                    <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-slate-100 flex items-center justify-center mb-3">
                      <FileSpreadsheet size={18} className="text-gray-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-600 mb-1">
                      No statements uploaded
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload bank statements to start reconciliation
                    </p>
                    {JSON.parse(localStorage.getItem("user") || "{}").account_type !== "organization" && (
                    <>
                    <input
                      type="file"
                      id="bank-statement-upload-3"
                      accept=".pdf,.xlsx,.xls,.csv"
                      onChange={handleUpload}
                      className="hidden"
                    />
                    <Button
                      onClick={() =>
                        document
                          .getElementById("bank-statement-upload-3")
                          ?.click()
                      }
                      className="h-7 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white"
                    >
                      <Upload size={10} className="mr-1.5" />
                      Upload Statement
                    </Button>
                    </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {statements.map((statement) => (
                      <div
                        key={statement.id}
                        className="relative overflow-hidden rounded-xl bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-300 group"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-50/0 to-slate-50/0 group-hover:from-gray-50/30 group-hover:to-slate-50/30 transition-all duration-300"></div>

                        <div className="relative p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className="p-2 rounded-lg bg-gradient-to-br from-gray-100 to-slate-100">
                                <FileSpreadsheet
                                  size={14}
                                  className="text-gray-600"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-semibold text-gray-800 truncate">
                                  {statement.filename}
                                </h4>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex items-center gap-1 text-[10px] text-gray-500">
                                    <Calendar size={8} />
                                    <span>
                                      {formatDate(statement.upload_date)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <div className="text-xs font-medium bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">
                                  {formatCurrency(statement.total_credits)}
                                </div>
                                <div className="text-[10px] text-gray-500">
                                  Credits
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs font-medium bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                                  {formatCurrency(statement.total_debits)}
                                </div>
                                <div className="text-[10px] text-gray-500">
                                  Debits
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleDeleteStatement(statement.id)
                                }
                                className="h-6 w-6 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 size={12} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Buyer Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-sm font-bold text-gray-800">
                {selectedBuyer?.buyer_name}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {selectedBuyer?.buyer_gst && `GST: ${selectedBuyer.buyer_gst}`}
              </DialogDescription>
            </DialogHeader>

            {selectedBuyer && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-green-50 rounded border border-green-200">
                    <div className="text-[10px] text-gray-600">Total Sales</div>
                    <div className="text-sm font-bold text-green-600">
                      {formatCurrency(selectedBuyer.total_sales)}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-primary-50 rounded border border-primary-200">
                    <div className="text-[10px] text-gray-600">Received</div>
                    <div className="text-sm font-bold text-primary-600">
                      {formatCurrency(selectedBuyer.total_received)}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-red-50 rounded border border-red-200">
                    <div className="text-[10px] text-gray-600">Outstanding</div>
                    <div className="text-sm font-bold text-red-600">
                      {formatCurrency(selectedBuyer.outstanding)}
                    </div>
                  </div>
                </div>

                {selectedBuyer.invoices?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-xs text-gray-700 mb-1">
                      Invoices
                    </h4>
                    <div className="border rounded">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-[10px] p-1.5">
                              Invoice
                            </TableHead>
                            <TableHead className="text-[10px] p-1.5">
                              Date
                            </TableHead>
                            <TableHead className="text-[10px] p-1.5 text-right">
                              Amount
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedBuyer.invoices.map((inv, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="p-1.5 text-xs font-mono">
                                {inv.invoice_no || "-"}
                              </TableCell>
                              <TableCell className="p-1.5 text-xs">
                                {formatDate(inv.invoice_date)}
                              </TableCell>
                              <TableCell className="p-1.5 text-right text-xs font-medium">
                                {formatCurrency(inv.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Supplier Details Dialog */}
        <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-sm font-bold text-gray-800">
                {selectedSupplier?.supplier_name}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {selectedSupplier?.supplier_gst &&
                  `GST: ${selectedSupplier.supplier_gst}`}
              </DialogDescription>
            </DialogHeader>

            {selectedSupplier && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-red-50 rounded border border-red-200">
                    <div className="text-[10px] text-gray-600">Total</div>
                    <div className="text-sm font-bold text-red-600">
                      {formatCurrency(selectedSupplier.total_purchases)}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-primary-50 rounded border border-primary-200">
                    <div className="text-[10px] text-gray-600">Paid</div>
                    <div className="text-sm font-bold text-primary-600">
                      {formatCurrency(selectedSupplier.total_paid)}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-orange-50 rounded border border-orange-200">
                    <div className="text-[10px] text-gray-600">Due</div>
                    <div className="text-sm font-bold text-orange-600">
                      {formatCurrency(selectedSupplier.balance_due)}
                    </div>
                  </div>
                </div>

                {selectedSupplier.invoices?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-xs text-gray-700 mb-1">
                      Purchase Invoices
                    </h4>
                    <div className="border rounded">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-[10px] p-1.5">
                              Invoice
                            </TableHead>
                            <TableHead className="text-[10px] p-1.5">
                              Date
                            </TableHead>
                            <TableHead className="text-[10px] p-1.5 text-right">
                              Amount
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedSupplier.invoices.map((inv, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="p-1.5 text-xs font-mono">
                                {inv.invoice_no || "-"}
                              </TableCell>
                              <TableCell className="p-1.5 text-xs">
                                {formatDate(inv.invoice_date)}
                              </TableCell>
                              <TableCell className="p-1.5 text-right text-xs font-medium">
                                {formatCurrency(inv.amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Transactions Mapping Dialog */}
        <Dialog
          open={transactionsDialogOpen}
          onOpenChange={setTransactionsDialogOpen}
        >
          <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                <FileSpreadsheet size={14} />
                {selectedStatement?.filename} -{" "}
                {transactionType === "credit" ? "Credits" : "Debits"}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Map transactions to{" "}
                {transactionType === "credit" ? "buyers" : "suppliers"}
              </DialogDescription>
            </DialogHeader>

            {statementTransactions ? (
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row items-center gap-2">
                  <div className="relative flex-1 w-full">
                    <Search
                      size={12}
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <Input
                      placeholder="Search transactions..."
                      value={transactionSearch}
                      onChange={(e) => setTransactionSearch(e.target.value)}
                      className="pl-7 h-7 text-xs"
                    />
                  </div>
                  <div className="flex gap-1 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllFiltered}
                      className="h-7 text-xs"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearSelection}
                      className="h-7 text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="border rounded max-h-[50vh] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white">
                      <TableRow className="bg-gray-50">
                        <TableHead className="w-8 p-1.5"></TableHead>
                        <TableHead className="text-[10px] p-1.5 w-16">
                          Date
                        </TableHead>
                        <TableHead className="text-[10px] p-1.5">
                          Description
                        </TableHead>
                        <TableHead className="text-[10px] p-1.5 text-right w-24">
                          Amount
                        </TableHead>
                        <TableHead className="text-[10px] p-1.5 w-40">
                          Map To
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getFilteredTransactions().map((txn) => (
                        <TableRow key={txn.index}>
                          <TableCell className="p-1.5">
                            <Checkbox
                              checked={selectedTransactions.includes(txn.index)}
                              onCheckedChange={() =>
                                toggleTransactionSelection(txn.index)
                              }
                              className="h-3 w-3"
                            />
                          </TableCell>
                          <TableCell className="p-1.5 text-xs">
                            {formatDate(txn.date)}
                          </TableCell>
                          <TableCell className="p-1.5">
                            <div className="text-xs">{txn.description}</div>
                            {txn.party_name && (
                              <div className="text-[10px] text-gray-500">
                                Party: {txn.party_name}
                              </div>
                            )}
                          </TableCell>
                          <TableCell
                            className={`p-1.5 text-right text-xs font-medium ${
                              transactionType === "credit"
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {formatAmount(
                              transactionType === "credit"
                                ? txn.credit
                                : txn.debit,
                            )}
                          </TableCell>
                          <TableCell className="p-1.5">
                            <Select
                              value={
                                transactionType === "credit"
                                  ? txn.mapped_buyer || "unmapped"
                                  : txn.mapped_supplier || "unmapped"
                              }
                              onValueChange={(value) =>
                                handleMapTransaction(
                                  txn.index,
                                  value === "unmapped" ? null : value,
                                )
                              }
                              disabled={savingMapping === txn.index}
                            >
                              <SelectTrigger className="w-full h-6 text-xs">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem
                                  value="unmapped"
                                  className="text-xs"
                                >
                                  -- Not Mapped --
                                </SelectItem>
                                {(transactionType === "credit"
                                  ? statementTransactions.buyers
                                  : statementTransactions.suppliers
                                )?.map((party) => (
                                  <SelectItem
                                    key={party.name}
                                    value={party.name}
                                    className="text-xs"
                                  >
                                    {party.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {getFilteredTransactions().length === 0 && (
                  <Alert className="bg-gray-50 border-gray-200">
                    <AlertCircle className="h-4 w-4 text-gray-400" />
                    <div>
                      <AlertTitle className="text-xs font-bold text-gray-700">
                        No Transactions
                      </AlertTitle>
                      <AlertDescription className="text-xs text-gray-600">
                        No {transactionType === "credit" ? "credit" : "debit"}{" "}
                        transactions found
                      </AlertDescription>
                    </div>
                  </Alert>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mb-2"></div>
                <p className="text-gray-500 text-xs">Loading transactions...</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Mapping Dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-sm font-bold text-gray-800">
                Bulk Map {selectedTransactions.length} Transactions
              </DialogTitle>
              <DialogDescription className="text-xs">
                Select a {transactionType === "credit" ? "buyer" : "supplier"}{" "}
                to map transactions
              </DialogDescription>
            </DialogHeader>
            <div className="py-3">
              <Select
                value={bulkMappingBuyer}
                onValueChange={setBulkMappingBuyer}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue
                    placeholder={`Select ${transactionType === "credit" ? "buyer" : "supplier"}...`}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(transactionType === "credit"
                    ? statementTransactions?.buyers
                    : statementTransactions?.suppliers
                  )?.map((party) => (
                    <SelectItem
                      key={party.name}
                      value={party.name}
                      className="text-xs"
                    >
                      {party.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setBulkDialogOpen(false)}
                className="h-7 text-xs"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkMap}
                disabled={!bulkMappingBuyer}
                className="h-7 text-xs bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white"
              >
                Map {selectedTransactions.length} Txns
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
